import { useEffect } from 'react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useBooking } from '@/hooks/bookings/useBooking';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { dateKeyInTimeZone } from '@/lib/date-values';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Non-UUID children of `/calendar/` that older bookmarks/sidebar links may
// point at. Route them to the live page rather than letting them fall
// through to this $id route and trigger a 400 retry-loop on
// `/bookings/<word>` plus a "Booking not found" screen.
const STATIC_ALIASES: Record<string, string> = {
  board: '/dashboard/dispatch',
  unscheduled: '/dashboard/dispatch',
};

export const Route = createFileRoute('/_dashboard/dashboard/calendar/$id')({
  beforeLoad: ({ params }) => {
    if (UUID_RE.test(params.id)) return;
    const alias = STATIC_ALIASES[params.id];
    throw redirect({ to: alias ?? '/dashboard/calendar', replace: true });
  },
  component: CalendarJobDetailPage,
});

function CalendarJobDetailPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const { data: booking, isLoading, isError } = useBooking(id);
  const companyTimezone = useCompanyTimezone();

  useEffect(() => {
    if (!booking) return;
    if (booking.scheduledStart && !booking.timezone && !companyTimezone) return;
    // Open the booking on the Dispatch cockpit: its detail Sheet auto-opens
    // from ?bookingId and the timeline pages to that date.
    void router.navigate({
      to: '/dashboard/dispatch',
      search: {
        date:
          dateKeyInTimeZone(booking.scheduledStart, booking.timezone || companyTimezone) ||
          undefined,
        // Leave the colour mode unset so the route default (Job type) applies —
        // pinning `status` here stuck in the URL and hid the job-type colours.
        colorBy: undefined,
        map: 'off' as const,
        bookingId: id,
        territoryId: undefined,
        jobTypeId: undefined,
        businessUnitId: undefined,
        priority: undefined,
        origin: undefined,
        exceptions: undefined,
      },
      replace: true,
    });
  }, [booking, companyTimezone, id, router]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading booking…</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="font-medium">Booking not found</p>
          <p className="text-sm text-muted-foreground">
            The booking you are looking for does not exist or was deleted.
          </p>
          <Button
            variant="outline"
            onClick={() =>
              void router.navigate({
                to: '/dashboard/dispatch',
                search: {
                  date: undefined,
                  colorBy: undefined,
                  map: 'off' as const,
                  bookingId: undefined,
                  territoryId: undefined,
                  jobTypeId: undefined,
                  businessUnitId: undefined,
                  priority: undefined,
                  origin: undefined,
                  exceptions: undefined,
                },
              })
            }
          >
            Go to Dispatch
          </Button>
        </div>
      </div>
    );
  }

  // Redirecting — show a brief loading state while navigation takes place.
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Opening booking on calendar…</p>
      </div>
    </div>
  );
}
